import { useCallback, useMemo, useState, type ReactNode } from "react";
import { useGetOne, useRecordContext } from "react-admin";
import { alpha, Box, Chip, Paper, Skeleton, Stack, Typography } from "@mui/material";
import GavelIcon from "@mui/icons-material/Gavel";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import BalanceIcon from "@mui/icons-material/Balance";
import type { Claimant, LaseredClaimantListItem } from "@/types";
import { useLaseredConditions } from "@/resources/policies/hooks/useLaseredConditions";
import { useContractLaser } from "@/resources/policies/hooks/useContractLaser";
import { policyDisplayLabel } from "@/resources/stoploss-claims/hooks/useStoplossClaimContext";
import { getEffectiveDeductible, useSelectedPolicyData } from "../../hooks/useCurrentPolicyData";
import { useDeductibleResolutionEvents, useDeductibleReviews } from "../../hooks/useDeductibleReviews";
import { DeductibleDisclosureRow } from "./DeductibleDisclosureRow";
import {
  ConditionDetail,
  ContractDetail,
  LaserDetail,
  PolicyStandardDetail,
  ReviewDetail,
} from "./DeductibleRowDetails";
import {
  buildDeductibleRows,
  heroLead,
  laserEffectiveDate,
  money,
  straightLaserFacts,
  type AsyncList,
  type DeductibleRow,
  type DeductibleRowKind,
} from "./deductibleRows";

const ICONS: Record<DeductibleRowKind, ReactNode> = {
  laser: <GavelIcon />,
  policy: <DescriptionOutlinedIcon />,
  conditional: <TrackChangesIcon />,
  contract: <ArticleOutlinedIcon />,
  review: <BalanceIcon />,
  modification: <DescriptionOutlinedIcon />,
};

const GroupHeading = ({ children, first = false }: { children: ReactNode; first?: boolean }) => (
  <Typography
    component="h2"
    sx={{
      fontSize: "0.6875rem",
      fontWeight: 700,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      color: "text.secondary",
      px: 0.25,
      pb: 0.75,
      mt: first ? 1.5 : 2,
    }}
  >
    {children}
  </Typography>
);

const Group = ({ children }: { children: ReactNode }) => (
  <Paper elevation={0} sx={{ border: 1, borderColor: "divider", borderRadius: 3, overflow: "hidden" }}>
    {children}
  </Paper>
);

/**
 * Claimant Deductible tab, V1 "summary + disclosure rows" (#7661, design
 * mockup-v6). The hero is the applied specific deductible for the selected
 * policy, from the shared resolver; under it two groups of rows explain why
 * (the applied rung and the rungs it overrides) and what could change it.
 * Nothing here writes to a laser: that stays on the policy's Lasered Claimants
 * tab (PO 2026-10-07). The page shell owns the review banner; the hold strip
 * here is the tab's own, and resolves the review in place.
 */
export const ClaimantDeductibleTab = () => {
  const claimant = useRecordContext<Claimant>();
  const { policy, policyId, policyData } = useSelectedPolicyData();
  const claimantId = claimant?.id ? String(claimant.id) : undefined;
  const ready = !!claimantId && !!policyId;

  const { amount, source, reviewPending } = getEffectiveDeductible(policyData, policy);

  const conditionsQuery = useLaseredConditions(policyId, claimantId, ready);
  const reviewsQuery = useDeductibleReviews(claimantId, policyId, ready);
  const { events } = useDeductibleResolutionEvents(claimantId, policyId, ready);

  // The straight laser's effective date and reason live on the lasered-claimants
  // row, not reliably on the claimant GET. One read, only when there is a laser.
  const laserFacts = straightLaserFacts(policyData);
  const { data: laserRow, isLoading: laserRowLoading } = useGetOne<LaseredClaimantListItem>(
    `policies/${policyId}/lasered-claimants`,
    { id: claimantId ?? "" },
    { enabled: ready && !!laserFacts, retry: false }
  );
  // The lead sentence and the laser row's subtitle carry the laser's effective
  // date. When the snapshot already has it, nothing waits; when that read is the
  // only source, they show skeletons until it lands rather than a shorter text
  // that then grows (the first-paint blink, Dillon 2026-10-28).
  const laserLoading = !!laserFacts && laserRowLoading && !laserEffectiveDate(source, policyData, undefined);
  // Only a lasered claimant's lead quotes that date; other sources' sentences do not wait.
  const leadLoading = laserLoading && source === "LASER";

  // The contract flags are omitempty (absent whenever false), so their absence
  // says nothing. Only a claimant with no resolved snapshot at all needs the
  // contract-laser read to fill them in.
  const snapshotLacksContract = !!policyData && policyData.effective_specific_deductible === undefined;
  const { contractLaser } = useContractLaser(policyId, claimantId, ready && snapshotLacksContract);
  const effectivePolicyData = useMemo(() => {
    if (!policyData || !contractLaser || !snapshotLacksContract) return policyData;
    return { ...policyData, ...contractLaser };
  }, [policyData, contractLaser, snapshotLacksContract]);

  const conditions: AsyncList<(typeof conditionsQuery.conditions)[number]> = conditionsQuery.isUnsupported
    ? { status: "ready", data: [] }
    : conditionsQuery.isLoading
      ? { status: "loading" }
      : conditionsQuery.error
        ? { status: "error" }
        : { status: "ready", data: conditionsQuery.conditions };
  const reviews: AsyncList<(typeof reviewsQuery.reviews)[number]> = reviewsQuery.isLoading
    ? { status: "loading" }
    : reviewsQuery.error
      ? { status: "error" }
      : { status: "ready", data: reviewsQuery.reviews };

  const policyLabel = policyDisplayLabel(policy);
  const groups = useMemo(
    () =>
      buildDeductibleRows({
        source,
        amount,
        reviewPending,
        policy,
        policyData: effectivePolicyData,
        conditions,
        reviews,
        laserRow,
        laserLoading,
        policyLabel,
      }),
    [
      source,
      amount,
      reviewPending,
      policy,
      effectivePolicyData,
      conditions,
      reviews,
      laserRow,
      laserLoading,
      policyLabel,
    ]
  );
  const lead = heroLead({
    source,
    reviewPending,
    policy,
    policyData: effectivePolicyData,
    conditions: conditions.status === "ready" ? conditions.data : [],
    reviews: reviews.status === "ready" ? reviews.data : [],
    laserRow,
  });

  // One row open at a time.
  const [openId, setOpenId] = useState<string | undefined>();
  const toggle = useCallback((id: string) => setOpenId((cur) => (cur === id ? undefined : id)), []);

  if (!claimant) return null;
  if (!policyId) {
    return (
      <Box data-testid="claimant-deductible-tab">
        <Paper
          elevation={0}
          sx={{ border: 1, borderColor: "divider", borderRadius: 3, p: 3, textAlign: "center", mt: 1.5 }}
        >
          <Typography variant="body2" color="text.secondary">
            Not linked to a policy.
          </Typography>
          <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 0.5 }}>
            The specific deductible is set per policy term. Link a policy from the header menu to see it here.
          </Typography>
        </Paper>
      </Box>
    );
  }

  const renderDetail = (row: DeductibleRow) => {
    switch (row.kind) {
      case "laser":
        return (
          <LaserDetail
            policyId={policyId}
            policyData={effectivePolicyData}
            laserRow={laserRow}
            effectiveDate={laserEffectiveDate(source, effectivePolicyData, laserRow)}
            events={events}
          />
        );
      case "policy":
        return <PolicyStandardDetail policy={policy} />;
      case "conditional":
        return row.condition ? <ConditionDetail condition={row.condition} policyId={policyId} events={events} /> : null;
      case "contract":
        return <ContractDetail policyId={policyId} policyData={effectivePolicyData} />;
      case "review":
        return row.review && claimantId ? (
          <ReviewDetail
            review={row.review}
            conditions={conditions.status === "ready" ? conditions.data : []}
            claimantId={claimantId}
          />
        ) : null;
    }
  };

  const retryFor = (row: DeductibleRow) =>
    row.status === "error"
      ? row.kind === "conditional"
        ? () => void conditionsQuery.refetch()
        : () => void reviewsQuery.refetch()
      : undefined;

  const renderGroup = (rows: DeductibleRow[], header?: ReactNode) => (
    <Group>
      {header}
      {rows.map((row, i) => (
        <DeductibleDisclosureRow
          key={row.id}
          row={row}
          icon={ICONS[row.kind]}
          open={openId === row.id}
          onToggle={() => toggle(row.id)}
          onRetry={retryFor(row)}
          last={i === rows.length - 1}
        >
          {openId === row.id ? renderDetail(row) : null}
        </DeductibleDisclosureRow>
      ))}
    </Group>
  );

  // The hero is the summary band of the "Why this amount" card (mockup-v7 B):
  // the sentence on the left, the applied amount on the right in the rung
  // amounts' column, at card-header weight, far from the rail's spend figure.
  // No hold strip here: the rail (or the collapsed summary bar) shows it on every
  // tab with the Resolve action; the band's chip and the review row mark the hold.
  const heroBand = (
    <Box
      data-testid="deductible-hero"
      sx={(t) => ({
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        px: 2,
        py: 1.5,
        bgcolor: alpha(t.palette.text.primary, t.palette.mode === "dark" ? 0.06 : 0.025),
        borderBottom: 1,
        borderColor: "divider",
      })}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>Specific deductible</Typography>
        {leadLoading ? (
          <Skeleton width={320} height={20} data-testid="deductible-hero-lead-loading" />
        ) : (
          <Typography sx={{ fontSize: "0.875rem", lineHeight: 1.5, mt: 0.25 }} data-testid="deductible-hero-lead">
            {lead}
          </Typography>
        )}
      </Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
        {reviewPending && (
          <Chip
            label="Under review"
            size="small"
            sx={(t) => ({
              bgcolor: alpha(t.palette.warning.main, 0.16),
              color: t.palette.warning.dark,
              fontWeight: 600,
              fontSize: "0.6875rem",
              height: 21,
            })}
          />
        )}
        <Typography
          component="div"
          data-testid="deductible-hero-amount"
          sx={{ fontSize: "1.25rem", fontWeight: 700, lineHeight: 1.2, fontVariantNumeric: "tabular-nums" }}
        >
          {source === "EXCLUDED" ? "Excluded" : money(amount)}
        </Typography>
      </Stack>
    </Box>
  );

  return (
    <Box data-testid="claimant-deductible-tab">
      <GroupHeading first>Why this amount</GroupHeading>
      {renderGroup(groups.why, heroBand)}

      <GroupHeading>What could change it</GroupHeading>
      {renderGroup(groups.couldChange)}
    </Box>
  );
};
